import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion } from 'react-bootstrap'

import { CourseVerticalCard } from 'components/ui/CourseCards'
import { AccordionToggle } from 'components/ui/AccordionToggle'
import { ICourseCard } from 'models/courseCard.model'
import { IEnrolCard } from 'models/enrolCard.model'
import { Slider } from 'components/ui/Slider'
import { useAccess } from 'contexts/AccessContext'
import { EmptyStateBanner } from 'components/ui/empty-states/empty-state-banner'
import { paths } from 'routes/paths'
import { useIsMobile } from 'hooks/useIsMobile'
import useSlidesToShow from 'hooks/useSlidesToShow'
import { CategoryHeader } from 'pages/Library/components/CategoryHeader/CategoryHeader'
import styles from './Folder.module.scss'

interface FolderProps {
  href?: string
  name: string
  courses: {
    course: ICourseCard
    enrol: IEnrolCard
  }[]
  totalCourses: number
}

export const Folder = ({ name, totalCourses, courses, href }: FolderProps) => {
  const swiping = useRef(false)
  const { t } = useTranslation()
  const { roles } = useAccess()

  const isMobile = useIsMobile()
  const sliderContainerRef = useRef<HTMLDivElement>(null)

  const slidesToShow = useSlidesToShow({
    containerRef: sliderContainerRef,
  })

  return (
    <Accordion defaultActiveKey={name}>
      <AccordionToggle eventKey={name}>
        <CategoryHeader
          title={name}
          count={totalCourses || 0}
          showMoreLink={href}
          showMoreText={t('newhome.buttons.viewMore')}
        />
      </AccordionToggle>
      <Accordion.Collapse eventKey={name}>
        {courses && courses.length > 0 ? (
          <div ref={sliderContainerRef} className={styles.sliderContainer}>
            <Slider
              afterChange={() => {
                swiping.current = false
              }}
              beforeChange={() => {
                swiping.current = true
              }}
              dots={false}
              slidesToScroll={1}
              centerMode={false}
              variableWidth
              arrows={!isMobile && courses.length > slidesToShow}
              infinite={courses.length > slidesToShow}
              swipe={courses.length > slidesToShow}
            >
              {courses.map(({ course, enrol }) => (
                <div key={course.id}>
                  {/* Is necessary to wrap the card with a div to avoid a bug with the margin */}
                  <div
                    style={{
                      marginRight: '24px',
                    }}
                  >
                    <CourseVerticalCard
                      course={course}
                      enrol={enrol}
                      enrolled={!roles.superadmin}
                      swiping={() => swiping.current}
                    />
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div style={{ paddingBottom: 32 }}>
            <EmptyStateBanner
              text={t('newhome.emptyStates.byCategory.category.text')}
              subtext={t('newhome.emptyStates.byCategory.category.subtext')}
              link={{
                href: paths.library.root,
                text: t('newhome.emptyStates.byCategory.category.link'),
              }}
            />
          </div>
        )}
      </Accordion.Collapse>
    </Accordion>
  )
}
